import { useState } from "react";
import type { ChangeBrief, Question } from "../../shared/schema";
import { CalendarIcon, CheckIcon, EditIcon, GripIcon, PlusIcon } from "../icons";

const answerLabels: Record<Question["answerType"], string> = {
  yes_no: "Yes / No",
  short_text: "Short answer",
  number: "Number",
  multiple_choice: "Multiple choice",
};

function Workflow() {
  const steps = ["Inspect", "Prepare", "Interview", "Decide"];
  return (
    <ol className="workflow" aria-label="Change workflow">
      {steps.map((step, index) => (
        <li className={index < 2 ? "is-done" : index === 2 ? "is-active" : ""} key={step}>
          <span className="step-marker">{index < 2 ? <CheckIcon /> : index + 1}</span>
          <span className="step-name">{step}</span>
          {index < steps.length - 1 ? <span className="step-line" /> : null}
        </li>
      ))}
    </ol>
  );
}

function FieldDiff({ change }: { change: ChangeBrief }) {
  return (
    <div className="field-diff" aria-label="Field change diff">
      <div className="diff-row removed"><span className="line-number">1</span><code>− {change.sourceField}</code><code>{change.sourceType}</code><code>-- email address of customer</code></div>
      <div className="diff-row added"><span className="line-number">2</span><code>+ {change.targetField}</code><code>{change.targetType}</code><code>-- primary contact detail</code></div>
      <div className="diff-row"><span className="line-number">3</span><code className="comment">-- type widened from {change.sourceType} to {change.targetType}</code></div>
      <div className="diff-row"><span className="line-number">4</span><code className="comment">-- column renamed</code></div>
    </div>
  );
}

interface ChangeWorkspaceProps {
  change: ChangeBrief;
  onQuestionsChange: (questions: Question[]) => void;
}

export function ChangeWorkspace({ change, onQuestionsChange }: ChangeWorkspaceProps) {
  const [editingId, setEditingId] = useState<string | null>(null);

  const updateQuestion = (id: string, text: string) => {
    onQuestionsChange(change.questions.map((question) => question.id === id ? { ...question, text } : question));
  };

  return (
    <main className="change-workspace">
      <section className="workspace-heading">
        <h1>{change.sourceField}</h1>
        <p>{change.summary}</p>
      </section>
      <Workflow />
      <section className="change-brief">
        <h2>Change brief</h2>
        <h3>Field change</h3>
        <FieldDiff change={change} />
        <h3>Affected assets ({change.affectedAssets.length})</h3>
        <ul className="asset-list">
          {change.affectedAssets.slice(0, 5).map((asset) => <li key={asset}>{asset}</li>)}
          <li className="more-assets">+ {change.affectedAssets.length - 5} more</li>
        </ul>
        <h3>Maintenance window</h3>
        <div className="maintenance"><CalendarIcon /><span>Sep 12, 2026 02:00 – 04:00 UTC</span><i /><span>Duration: 2h</span></div>
      </section>
      <section className="question-section">
        <div className="section-heading-row">
          <h2>Questions for the owner</h2>
          <span>Answer type</span>
        </div>
        <ol className="question-list">
          {change.questions.map((question, index) => (
            <li key={question.id}>
              <span className="question-number">{index + 1}</span>
              {editingId === question.id ? (
                <input
                  autoFocus
                  value={question.text}
                  onChange={(event) => updateQuestion(question.id, event.target.value)}
                  onBlur={() => setEditingId(null)}
                  onKeyDown={(event) => { if (event.key === "Enter") setEditingId(null); }}
                  aria-label={`Edit question ${index + 1}`}
                />
              ) : <span className="question-text">{question.text}</span>}
              <span className="answer-type">{answerLabels[question.answerType]}</span>
              <button type="button" aria-label={`Edit question ${index + 1}`} onClick={() => setEditingId(question.id)}><EditIcon /></button>
              <span className="grip" aria-hidden="true"><GripIcon /></span>
            </li>
          ))}
        </ol>
        <button className="add-question" type="button" onClick={() => onQuestionsChange([...change.questions, { id: `question-${change.questions.length + 1}`, text: "What else should the migration plan account for?", answerType: "short_text" }])}>
          <PlusIcon /> Add question
        </button>
      </section>
    </main>
  );
}
